/**
 * Copyright IBM Corp. 2020, 2025
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useState, useEffect } from 'react';
import PropTypes from '../../_virtual/index.js';
import cx from 'classnames';
import { Tag, ComposedModal, ModalHeader, Search, ModalBody } from '@carbon/react';
import { pkg } from '../../settings.js';
import { prepareProps } from '../../global/js/utils/props-helper.js';
import { usePortalTarget } from '../../global/js/hooks/usePortalTarget.js';

const componentName = 'TagSetModal';
const blockClass = `${pkg.prefix}--tag-set-modal`;

// Default values for props
const defaults = {
  // required for accessibility if using hasScrollingContent
  modalAriaLabel: 'List of all tags',
  // marked as required by TagSet if needed, default used to satisfy <Search /> component
  searchLabel: ''
};
const TagSetModal = _ref => {
  let {
    // The component props, in alphabetical order (for consistency).

    allTags,
    className,
    title,
    modalAriaLabel = defaults.modalAriaLabel,
    onClose,
    open,
    portalTarget: portalTargetIn,
    searchLabel = defaults.searchLabel,
    searchPlaceholder,
    // Collect any other property values passed in.
    ...rest
  } = _ref;
  const [filteredModalTags, setFilteredModalTags] = useState([]);
  const [search, setSearch] = useState('');
  const renderPortalUse = usePortalTarget(portalTargetIn);
  useEffect(() => {
    let newFilteredModalTags = [];
    if (open) {
      if (search === '' && allTags) {
        newFilteredModalTags = allTags?.slice(0);
      } else {
        const lCaseSearch = search.toLocaleLowerCase();
        allTags?.forEach(tag => {
          const dataSearch = tag['data-search']?.toLocaleLowerCase()?.indexOf(lCaseSearch);
          const labelSearch = tag.label?.toLocaleLowerCase()?.indexOf(lCaseSearch);
          if (dataSearch > -1 || labelSearch > -1) {
            newFilteredModalTags.push(tag);
          }
        });
      }
    }
    setFilteredModalTags(newFilteredModalTags);
  }, [allTags, open, search]);
  const handleSearch = ev => {
    setSearch(ev.target.value || '');
  };
  return renderPortalUse(/*#__PURE__*/React__default.createElement(ComposedModal, _extends({}, rest, {
    containerClassName: `${blockClass}__container`,
    className: cx(className, `${blockClass}`),
    size: "sm",
    open,
    onClose
  }), /*#__PURE__*/React__default.createElement(ModalHeader, {
    className: `${blockClass}__header`,
    closeModal: onClose,
    title: title
  }, /*#__PURE__*/React__default.createElement(Search, {
    "data-modal-primary-focus": true,
    className: `${blockClass}__search`,
    labelText: searchLabel,
    placeholder: searchPlaceholder,
    onChange: handleSearch,
    size: "lg"
  })), /*#__PURE__*/React__default.createElement(ModalBody, {
    className: `${blockClass}__body`,
    hasForm: true,
    hasScrollingContent: true,
    "aria-label": modalAriaLabel
  }, filteredModalTags.map((_ref2, index) => {
    let {
      label,
      ...other
    } = _ref2;
    return /*#__PURE__*/React__default.createElement(Tag, _extends({}, other, {
      key: `all-tags-${index}`
    }), label);
  }), /*#__PURE__*/React__default.createElement("div", {
    className: `${blockClass}__fade`
  }))));
};
TagSetModal.propTypes = {
  allTags: PropTypes.arrayOf(PropTypes.shape({
    ...prepareProps(Tag.propTypes, 'filter'),
    label: PropTypes.string.isRequired
  })),
  className: PropTypes.string,
  onClose: PropTypes.func,
  open: PropTypes.bool,
  portalTarget: PropTypes.node,
  searchLabel: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  title: PropTypes.string
};
TagSetModal.displayName = componentName;

export { TagSetModal };
